import type { Action } from '~/domains/actions/types'
import { isSameDay } from '~/utils/date'

type TaskDueSection = 'overdue' | 'today' | 'week' | 'later' | 'unscheduled'

const taskDueSectionLabels: Record<TaskDueSection, string> = {
  overdue: 'En retard',
  today: "Aujourd'hui",
  week: 'Cette semaine',
  later: 'Semaines suivantes',
  unscheduled: 'Sans échéance'
}

export type TaskSection = {
  key: TaskDueSection | 'treated'
  /** Null for a flat list without heading */
  label: string | null
  tasks: Action[]
}

const sectionOrder: TaskDueSection[] = [
  'overdue',
  'today',
  'week',
  'later',
  'unscheduled'
]

const endOfWeek = (now: Date) => {
  const end = new Date(now)
  const day = end.getDay()
  const daysUntilSunday = day === 0 ? 0 : 7 - day
  end.setDate(end.getDate() + daysUntilSunday)
  end.setHours(23, 59, 59, 999)
  return end
}

export const getTaskDueDate = (task: Action): Date | null => {
  if (!task.datetime) {
    return null
  }
  const date = new Date(task.datetime)
  return Number.isNaN(date.getTime()) ? null : date
}

export const isTaskOverdue = (task: Action, now = new Date()): boolean => {
  const due = getTaskDueDate(task)
  return due !== null && due.getTime() < now.getTime()
}

const getTaskDueSection = (task: Action, now = new Date()): TaskDueSection => {
  const due = getTaskDueDate(task)
  if (due === null) {
    return 'unscheduled'
  }
  if (due.getTime() < now.getTime()) {
    return 'overdue'
  }
  if (isSameDay(due, now)) {
    return 'today'
  }
  if (due.getTime() <= endOfWeek(now).getTime()) {
    return 'week'
  }
  return 'later'
}

export const groupTasksByDue = (
  tasks: Action[],
  now = new Date()
): TaskSection[] => {
  const buckets = new Map<TaskDueSection, Action[]>()
  for (const task of tasks) {
    const section = getTaskDueSection(task, now)
    const bucket = buckets.get(section) ?? []
    bucket.push(task)
    buckets.set(section, bucket)
  }

  return sectionOrder
    .filter((key) => (buckets.get(key) ?? []).length > 0)
    .map((key) => ({
      key,
      label: taskDueSectionLabels[key],
      tasks: buckets.get(key) ?? []
    }))
}
